import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, TrendingUp, Clock, CheckCircle, FileText, Zap } from 'lucide-react';
import Header from '../Layout/Header';
import NewScreening from './NewScreening';
import ScreeningList from './ScreeningList';
import Alert from '../Common/Alert';
import UpgradePrompt from '../Common/UpgradePrompt';
import { screeningAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const { fetchUser, user } = useAuth();
  const [screenings, setScreenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    processing: 0,
    avgMatchScore: 0
  });

  useEffect(() => {
    fetchScreenings();
  }, []);

  const fetchScreenings = async () => {
    try {
      const response = await screeningAPI.getAll();
      const data = response.data;
      setScreenings(data);
      
      // Calculate stats
      const completed = data.filter(s => s.status === 'COMPLETED').length;
      const processing = data.filter(s => s.status === 'PROCESSING').length;
      
      setStats({
        total: data.length,
        completed,
        processing,
        avgMatchScore: completed > 0 ? 85 : 0 // Mock for now
      });
    } catch (err) {
      setError('Failed to load screenings');
    } finally {
      setLoading(false);
    }
  };

  const handleScreeningComplete = async (screening) => {
    setSuccess('🎉 Screening completed successfully! Check your results.');
    await fetchUser();
    await fetchScreenings();
    
    // Auto-navigate to results after 2 seconds
    setTimeout(() => {
      navigate(`/results/${screening.id}`);
    }, 2000);
  };

  const handleScreeningClick = (id) => {
    navigate(`/results/${id}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header />
      <UpgradePrompt />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Welcome back, <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{user?.fullName?.split(' ')[0]}</span>! 👋
          </h1>
          <p className="text-gray-600 text-lg">Screen candidates in seconds with AI-powered analysis</p>
        </div>

        {error && <Alert type="error" message={error} onClose={() => setError('')} />}
        {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

        {/* Low Credits Warning for Free Users */}
        {(user?.subscriptionPlan === 'FREE' || !user?.subscriptionPlan) && (user?.credits || 0) <= 2 && (
          <div className="mb-6 p-4 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">
                  {user?.credits === 0 ? "You're out of credits!" : `Only ${user?.credits} credit${user?.credits !== 1 ? 's' : ''} remaining`}
                </p>
                <p className="text-sm text-gray-600">Upgrade to Professional for 50 screenings/month</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/pricing')}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:shadow-lg transition flex items-center gap-2"
            >
              <Zap className="w-4 h-4" />
              Upgrade Now
            </button>
          </div>
        )}
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100 hover:shadow-xl transition">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-sm text-gray-500 font-medium">Total</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{stats.total}</div>
            <div className="text-sm text-gray-500">Screenings</div>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100 hover:shadow-xl transition">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-sm text-gray-500 font-medium">Completed</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{stats.completed}</div>
            <div className="text-sm text-gray-500">Successful</div>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100 hover:shadow-xl transition">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <span className="text-sm text-gray-500 font-medium">Processing</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{stats.processing}</div>
            <div className="text-sm text-gray-500">In Progress</div>
          </div>

          <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-md p-6 text-white hover:shadow-xl transition">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm text-white/80 font-medium">Avg Match</span>
            </div>
            <div className="text-3xl font-bold mb-1">{stats.avgMatchScore}%</div>
            <div className="text-sm text-white/80">Success Rate</div>
          </div>
        </div>

        {/* Quick Actions */}
        {stats.total === 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-dashed border-blue-200 rounded-2xl p-8 mb-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Ready to get started?</h3>
            <p className="text-gray-600 mb-4">Upload your first job description and resumes to see the magic! ✨</p>
            <div className="flex items-center justify-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-500" />
                <span>60 seconds screening</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>AI-powered analysis</span>
              </div>
            </div>
          </div>
        )}
        
        <NewScreening onSuccess={handleScreeningComplete} onError={setError} />
        
        <ScreeningList 
          screenings={screenings} 
          loading={loading}
          onScreeningClick={handleScreeningClick}
        />
      </div>
    </div>
  );
};

export default Dashboard;